/**
 * MermaidDiagram - Renders Mermaid diagrams with Martha theme
 *
 * Features:
 * - Martha-themed colors and styling
 * - Zoom in/out controls
 * - Download as SVG
 * - Fullscreen mode
 * - Error handling with code preview
 */

import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { initializeMermaid } from '../../config/mermaid-theme';
import { ZoomIn, ZoomOut, Download, Maximize2, X } from 'lucide-react';

// SVG render cache — avoids re-rendering identical charts on re-mount.
// Cleared on brand change so diagrams re-render with the new theme.
const svgCache = new Map<string, string>();
if (typeof window !== 'undefined') {
  window.addEventListener('mermaid-theme-change', () => svgCache.clear());
}

interface MermaidDiagramProps {
  chart: string;
  className?: string;
}

export function MermaidDiagram({ chart, className = '' }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [brandTick, setBrandTick] = useState(0);

  useEffect(() => {
    const onBrand = () => setBrandTick(t => t + 1);
    window.addEventListener('mermaid-theme-change', onBrand);
    return () => window.removeEventListener('mermaid-theme-change', onBrand);
  }, []);

  useEffect(() => {
    initializeMermaid();

    // Check cache first
    const cached = svgCache.get(chart);
    if (cached) {
      setSvg(cached);
      setError(null);
      setIsLoading(false);
      return;
    }

    const renderDiagram = async () => {
      setIsLoading(true);
      try {
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg: rendered } = await mermaid.render(id, chart);
        svgCache.set(chart, rendered);
        setSvg(rendered);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
        console.error('Mermaid render error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    renderDiagram();
  }, [chart, brandTick]);

  const handleZoomIn = () => setZoom((z) => Math.min(2, z + 0.25));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, z - 0.25));
  const handleZoomReset = () => setZoom(1);

  const handleDownload = () => {
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.svg';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 rounded-xl border border-line bg-surface ${className}`}>
        <div className="flex items-center gap-2 text-neutral-500">
          <div className="w-4 h-4 border-2 border-coral-300 border-t-coral-500 rounded-full animate-spin" />
          <span className="text-sm">Rendering diagram...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`p-4 rounded-xl border border-red-200 bg-red-50 ${className}`}>
        <div className="flex items-start gap-2 mb-2">
          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <X size={12} className="text-red-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-red-800">Diagram Error</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
        <pre className="mt-3 p-3 text-xs text-red-700 bg-red-100 rounded-lg overflow-auto max-h-32">
          {chart}
        </pre>
      </div>
    );
  }

  return (
    <>
      <div className={`relative group ${className}`}>
        {/* Toolbar - appears on hover */}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button
            onClick={handleZoomOut}
            className="p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
            title="Zoom out"
          >
            <ZoomOut size={14} className="text-neutral-600" />
          </button>
          <button
            onClick={handleZoomReset}
            className="px-2 py-1 text-xs bg-white/90 rounded-lg shadow-sm text-neutral-600 hover:bg-white transition-colors font-medium"
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            onClick={handleZoomIn}
            className="p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
            title="Zoom in"
          >
            <ZoomIn size={14} className="text-neutral-600" />
          </button>
          <div className="w-px h-4 bg-neutral-300 mx-1" />
          <button
            onClick={toggleFullscreen}
            className="p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
            title="Fullscreen"
          >
            <Maximize2 size={14} className="text-neutral-600" />
          </button>
          <button
            onClick={handleDownload}
            className="p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
            title="Download SVG"
          >
            <Download size={14} className="text-neutral-600" />
          </button>
        </div>

        {/* Diagram container */}
        <div
          ref={containerRef}
          className="overflow-auto rounded-xl border border-line bg-surface p-6"
        >
          <div
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
            className="transition-transform duration-200"
          >
            <div
              dangerouslySetInnerHTML={{ __html: svg }}
              className="mermaid-container flex items-center justify-center"
            />
          </div>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
            onClick={toggleFullscreen}
          />
          <div className="fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Fullscreen header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface-muted">
              <span className="text-sm font-medium text-neutral-700">Diagram View</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleZoomOut}
                  className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                  title="Zoom out"
                >
                  <ZoomOut size={16} className="text-neutral-600" />
                </button>
                <button
                  onClick={handleZoomReset}
                  className="px-2 py-1 text-xs rounded-lg hover:bg-neutral-100 transition-colors font-medium text-neutral-600"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  onClick={handleZoomIn}
                  className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                  title="Zoom in"
                >
                  <ZoomIn size={16} className="text-neutral-600" />
                </button>
                <div className="w-px h-5 bg-neutral-200 mx-1" />
                <button
                  onClick={handleDownload}
                  className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                  title="Download SVG"
                >
                  <Download size={16} className="text-neutral-600" />
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors ml-2"
                  title="Close fullscreen"
                >
                  <X size={16} className="text-neutral-600" />
                </button>
              </div>
            </div>

            {/* Fullscreen content */}
            <div className="flex-1 overflow-auto p-8">
              <div
                style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
                className="transition-transform duration-200 min-h-full"
              >
                <div
                  dangerouslySetInnerHTML={{ __html: svg }}
                  className="mermaid-container flex items-center justify-center"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mermaid diagram styling */}
      <style>{`
        .mermaid-container svg {
          max-width: 100%;
          height: auto;
        }

        .mermaid-container .node rect,
        .mermaid-container .node circle,
        .mermaid-container .node ellipse,
        .mermaid-container .node polygon {
          stroke-width: 2px;
        }

        .mermaid-container .edgePath path {
          stroke-width: 2px;
        }

        .mermaid-container .label {
          font-family: 'Inter', system-ui, sans-serif;
        }

        .mermaid-container .node:hover rect,
        .mermaid-container .node:hover circle {
          filter: brightness(0.95);
          cursor: pointer;
        }

        .mermaid-container * {
          transition: fill 0.2s ease, stroke 0.2s ease;
        }
      `}</style>
    </>
  );
}

export default MermaidDiagram;
